<template>
    <div>
        <div class="card">
            <div class="card-body mb-3">

                <div class="d-flex justify-content-between mb-3">
                    <h4 class="mb-0 d-block text-lg font-medium">Account Balance<span
                        class="ml-3 text-info">ZAR {{accountBalance | money}}</span></h4>
                    <span class="d-block">
                        <a
                            class="text-primary"
                            href="#"
                            @click.prevent="viewStatement"
                            :disabled="loadingStatement"
                        >
                            {{ loadingStatement ? "Loading..." : "View Statement"}}
                        </a>
                        <a
                            v-if="app.state.user.support"
                            class="text-primary" href="#"
                            @click.prevent="toggleNotesPanel"
                        >
                            &middot; View Notes
                        </a>
                    </span>
                </div>

                <notes-panel
                    title="Account Notes"
                    :show="showAccountNotes"
                    @close-notes-panel="toggleNotesPanel"
                ></notes-panel>

                <div class="table-responsive">
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th class="text-center no-wrap">#</th>
                                <th class="no-wrap">Date</th>
                                <th>Description</th>
                                <th class="no-wrap">Category</th>
                                <th class="text-right no-wrap">Debits</th>
                                <th class="text-right no-wrap">Credits</th>
                                <th></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="tx in mutableTransactions" :key="tx.id">
                                <td class="text-center">
                                    <div v-if="loadingDocument && tx.id===selectedTransaction" class="spinner">
                                        <div class="double-bounce1"></div>
                                        <div class="double-bounce2"></div>
                                    </div>
                                    <div v-else>
                                        <a
                                            v-if="app.state.user.support"
                                            href="#"
                                            @click.prevent="recompileSourceDocument(tx.id)"
                                        >
                                            {{tx.id}}
                                        </a>
                                        <span v-else>{{tx.id}}</span>
                                    </div>
                                </td>
                                <td style="white-space:nowrap">{{tx.date | dateNumeric}}</td>
                                <td>{{tx.description}}</td>
                                <td>
                                    <span style="white-space:nowrap" :class="categoryClass(tx.type)">
                                        {{tx.category | titlecase}}
                                    </span>
                                </td>
                                <td class="text-right font-medium">
                                    <span class="text-danger" v-if="tx.type==='debit'">
                                        {{tx.total_incl | money}}
                                    </span>
                                </td>
                                <td class="text-right font-medium">
                                    <span class="text-success" v-if="tx.type==='credit'">
                                        {{tx.total_incl | money}}
                                    </span>
                                </td>
                                <td>
                                    <a
                                        v-if="tx.has_source_document"
                                        title="View Invoice"
                                        href="#"
                                        @click.prevent="viewTransactionDocument(tx.id)"
                                    >
                                        <div v-if="loadingDocument && tx.id===selectedTransaction" class="spinner">
                                            <div class="double-bounce1"></div>
                                            <div class="double-bounce2"></div>
                                        </div>
                                        <i v-else class="ml-2 mdi mdi-arrow-collapse-down"></i>
                                    </a>
                                </td>
                                <td>
                                    <a
                                        v-if="tx.has_source_document"
                                        title="Email Invoice"
                                        href="#"
                                        @click.prevent="showEmailDocumentModal(tx.id)"
                                    >
                                        <i class="mdi mdi-send"></i>
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div v-if="app.state.user.support">
                <button
                    class="btn btn-primary w-100 btn-sm mt-3"
                    @click.prevent="$refs.createTransactionModal.showTransactionCreateForm"
                >
                    Add Transaction
                </button>

                <create-subscription-transaction
                    :subscription-id="subscription.id"
                    ref="createTransactionModal"
                    @new-transaction-added="insertNewTransactionInList"
                ></create-subscription-transaction>
            </div>
        </div>

        <div class="card">
            <div class="card-body">
                <div class="mb-3">
                    <h4 class="mb-0 d-block text-lg font-medium">Billing Settings</h4>
                    <div class="form mt-2 mb-2">
                        <div class="form-group">
                            <label for="invoice_recipients" class="control-label col-form-label">
                                Invoice Recipients
                            </label>
                            <input
                                type="text"
                                class="form-control"
                                id="invoice_recipients"
                                placeholder=""
                                v-model="invoiceRecipientsForm.invoice_recipients"
                                :class="{'is-invalid': invoiceRecipientsForm.errors.has('invoice_recipients')}"
                            />
                            <span
                                class="invalid-feedback"
                                v-show="invoiceRecipientsForm.errors.has('invoice_recipients')"
                            >
                                {{ invoiceRecipientsForm.errors.get("invoice_recipients") }}
                            </span>
                            <small class="d-block text-muted mt-1">
                                Specify the email addresses you want any invoices to be sent to. (Separate with commas)
                            </small>
                        </div>
                        <div class="form-group">
                            <button @click="saveInvoiceRecipients()" class="btn btn-primary pull-right">
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!--Send Document Via Email - Modal Form-->
        <div
            id="provide-email-modal"
            class="modal fade"
            tabindex="-1"
            role="dialog"
            style="display: none;"
        >
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h4 class="modal-title font-normal" id="modalTitle">
                            Send Invoice
                        </h4>
                        <button type="button" class="close" data-dismiss="modal" aria-hidden="true">×</button>
                    </div>
                    <div class="modal-body">
                        <div class="form">
                            <div class="form-group">
                                <label>Send To (Name of Recipient)</label>
                                <input
                                    class="form-control"
                                    type="text"
                                    v-model="provideEmailForm.name"
                                    placeholder="Specify email to send to"
                                    :class="{'is-invalid': provideEmailForm.errors.has('name')}"
                                />
                                <span
                                    class="invalid-feedback w-auto"
                                    v-show="provideEmailForm.errors.has('name')"
                                >
                                    {{ provideEmailForm.errors.get("name") }}
                                </span>
                            </div>
                            <div class="form-group">
                                <label>Email Address</label>
                                <input
                                    class="form-control"
                                    type="email"
                                    v-model="provideEmailForm.email"
                                    placeholder="Specify email to send to"
                                    :class="{'is-invalid': provideEmailForm.errors.has('email')}"
                                />
                                <span
                                    class="invalid-feedback w-auto"
                                    v-show="provideEmailForm.errors.has('email')"
                                >
                                    {{ provideEmailForm.errors.get("email") }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button
                            class="btn btn-success waves-effect waves-light"
                            @click.prevent="sendDocument"
                            :disabled="provideEmailForm.busy"
                        >
                            {{provideEmailForm.busy ? "Sending ..." : "Send"}}
                        </button>
                        <button
                            type="button"
                            class="btn btn-default waves-effect"
                            data-dismiss="modal"
                            :disabled="provideEmailForm.busy"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CreateSubscriptionTransaction from "../../settings/subscription-account/create-subscription-transaction";

export default {
    name: "subscription-account",
    props: [ "subscription" ],
    component: [ CreateSubscriptionTransaction ],
    data () {
        return {
            selectedTransaction: null,
            loadingDocument: false,
            loadingStatement: false,
            provideEmailForm: new AppForm({}),
            mutableTransactions: [],
            accountBalance: this.subscription.account_balance,
            notesUrl: "/subscription/" + this.subscription.id + "/account/notes",
            showAccountNotes: false,
            invoiceRecipientsForm: new AppForm({
                invoice_recipients: this.subscription.invoice_recipients
            })
        };
    },
    methods: {
        fetchTransactions () {
            axios.get("/subscriptions/" + this.subscription.id + "/transactions")
            .then(response => {
                this.mutableTransactions = response.data;
            });
        },
        showEmailDocumentModal (txId) {
            this.provideEmailForm = new AppForm({
                "name": App.state.user.first_name,
                "email": App.state.user.email,
                "selectedTransaction": txId
            });
            $("#provide-email-modal").modal("show");
        },
        hideEmailDocumentModal () {
            this.provideEmailForm = new AppForm({});
            $("#provide-email-modal").modal("hide");
        },
        toggleNotesPanel () {
            this.showAccountNotes = this.showAccountNotes !== true;
        },
        categoryClass (type) {
            return {
                "label label-rouded label-sm text-right": true,
                "label-info": type === "debit",
                "label-success": type === "credit"
            };
        },
        insertNewTransactionInList (transaction, accountBalance) {
            this.mutableTransactions.unshift(transaction);
            this.accountBalance = accountBalance;
        },
        viewStatement () {
            if (this.loadingStatement) {
                // Statement is already busy generating
                return;
            }

            this.loadingStatement = true;
            axios.get("/subscriptions/" + this.subscription.id + "/statement/show")
            .then((response) => {
                window.open(response.data, "_blank");
                this.loadingStatement = false;
            }).catch((errors) => {
                toastr.error("Could not open document!");
                this.loadingStatement = false;
            });
        },
        viewTransactionDocument (txId) {
            this.selectedTransaction = txId;
            this.loadingDocument = true;
            axios.get("/subscriptions/billing/transactions/" + txId + "/document/show")
            .then((response) => {
                window.open(response.data, "_blank");
                this.loadingDocument = false;
            }).catch((errors) => {
                toastr.error("Could not open document!");
                this.loadingDocument = false;
            });
        },
        recompileSourceDocument (txId) {
            this.selectedTransaction = txId;
            this.loadingDocument = true;
            axios.get("/subscriptions/billing/transactions/" + txId + "/document/recompile")
            .then((response) => {
                window.open(response.data, "_blank");
                this.loadingDocument = false;
            }).catch((errors) => {
                toastr.error("Could not recompile document!");
                this.loadingDocument = false;
            });
        },
        sendDocument () {
            App.post("/subscriptions/billing/transactions/" + this.provideEmailForm.selectedTransaction + "/document/email", this.provideEmailForm)
            .then((response) => {
                this.hideEmailDocumentModal();
                toastr.success("Email sent!");
            }).catch((errors) => {
                toastr.error("Document could not be sent!");
            });
        },
        saveInvoiceRecipients () {
            App.patch("/settings/billing/subscription/" + this.subscription.id + "/invoice-recipients/update", this.invoiceRecipientsForm)
            .then(() => {
                toastr.success("Saved!");
            })
            .catch((errors) => {
                toastr.error("Error saving billing settings!");
            });
        }
    },
    mounted () {
        this.fetchTransactions();
    }
};
</script>

<style scoped>
    .spinner {
        width: 16px;
        height: 16px;
        position: relative;
        margin: 0 0 0 6px !important;
    }

    .double-bounce1, .double-bounce2 {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background-color: #333;
        opacity: 0.6;
        position: absolute;
        top: 0;
        left: 0;

        -webkit-animation: sk-bounce 2.0s infinite ease-in-out;
        animation: sk-bounce 2.0s infinite ease-in-out;
    }

    .double-bounce2 {
        -webkit-animation-delay: -1.0s;
        animation-delay: -1.0s;
    }

    @-webkit-keyframes sk-bounce {
        0%, 100% {
            -webkit-transform: scale(0.0)
        }
        50% {
            -webkit-transform: scale(1.0)
        }
    }

    @keyframes sk-bounce {
        0%, 100% {
            transform: scale(0.0);
            -webkit-transform: scale(0.0);
        }
        50% {
            transform: scale(1.0);
            -webkit-transform: scale(1.0);
        }
    }
</style>
